"use client";

import { Download, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const playbookItems = [{
  index: "01",
  title: "OTA Revenue Management",
  steps: ["Optimize channel mix to reduce dependency on high-commission platforms.", "Implement dynamic rate strategies based on real-time demand.", "Ensure content parity across all booking channels.", "Monitor competitor pricing to maintain market positioning.", "Regularly audit listing performance and conversion metrics."]
}, {
  index: "02",
  title: "Hospitality Tech Stack",
  steps: ["Evaluate current PMS and Channel Manager integration efficiency.", "Recommend scalable solutions for automated revenue tasks.", "Streamline data flow between booking engines and accounting.", "Implement CRM tools to capture guest data for direct marketing.", "Ensure all tech investments provide measurable ROI."]
}, {
  index: "03",
  title: "Revenue Audits",
  steps: ["Conduct deep-dive analysis of historical booking patterns.", "Identify leakage points in the current distribution strategy.", "Review rate parity compliance across all third-party sites.", "Assess pricing elasticity during peak and off-peak seasons.", "Provide actionable insights to boost RevPAR and GOPPAR."]
}, {
  index: "04",
  title: "Direct Booking Growth",
  steps: ["Optimize website conversion paths for seamless booking.", "Leverage loyalty programs to incentivize direct reservations.", "Implement targeted digital ad campaigns for high-intent users.", "Use retargeting strategies to capture abandoned bookings.", "Analyze traffic sources to prioritize high-conversion channels."]
}, {
  index: "05",
  title: "Rate Parity Strategy",
  steps: ["Monitor rate discrepancies across all OTA platforms.", "Automate alerts for unauthorized rate undercutting.", "Maintain consistent pricing to protect brand integrity.", "Negotiate better terms with distribution partners.", "Ensure direct booking channels offer the best value proposition."]
}, {
  index: "06",
  title: "Market Forecasting",
  steps: ["Utilize predictive analytics for seasonal demand planning.", "Adjust inventory availability based on local event calendars.", "Monitor macroeconomic trends affecting travel behavior.", "Develop flexible pricing models for varying occupancy levels.", "Review forecast accuracy to refine future revenue strategies."]
}, {
  index: "07",
  title: "Consultation Process",
  steps: ["Schedule an initial discovery call to assess property needs.", "Perform a comprehensive audit of current revenue operations.", "Develop a customized strategy tailored to your hotel's goals.", "Execute implementation with ongoing performance monitoring.", "Review monthly reports to ensure continuous revenue growth."]
}];
export default function Revenuefaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1275" id="rateparityfaq" className="bg-background text-foreground relative py-32 md:py-44 overflow-hidden" style={{
    clipPath: "polygon(0 7%, 100% 0, 100% 100%, 0 100%)"
  }}>
      <div ref={ref} className={`container mx-auto px-6 md:px-12 max-w-[1400px] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="block w-6 h-px bg-primary"></span>
                <span className="font-default text-[0.75rem] tracking-[0.18em] uppercase text-muted-foreground">
                  Expertise 05 
                </span>
              </div>

              {}
              <h2 className="font-default uppercase text-foreground text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.92] tracking-[-0.005em] mb-8">
                The{" "}
                <em className="not-italic">
                  <span className="italic text-secondary">STRATEGY</span>
                </em>
                .
              </h2>

              {}
              <p className="font-default text-[clamp(1.0625rem,1.2vw,1.25rem)] leading-[1.6] text-foreground/70 max-w-md">
                Revenue management, tech implementation, and audit protocols. Everything hotel owners need to maximize profitability.
              </p>

              {}
              <div className="hidden lg:block mt-12 font-default text-[0.6875rem] tracking-[0.15em] uppercase text-muted-foreground">
                HotelRevBaBa / Revenue Consulting
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {playbookItems.map((item, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-foreground/20 last:border-b-0 group">
                  <AccordionTrigger className="hover:no-underline py-0 [&>svg]:hidden">
                    <div className="flex items-center justify-between w-full min-h-[96px] py-6 gap-6 group-hover:pl-2 transition-all duration-200">
                      <div className="flex items-center gap-6 md:gap-10 flex-1">
                        <span className="font-default text-sm md:text-base text-secondary tracking-[0.1em] shrink-0">
                          {item.index} 
                        </span>
                        <h3 className="font-default uppercase text-foreground text-[clamp(1.25rem,2.4vw,2.25rem)] leading-[1] tracking-[0] text-left transition-transform duration-200 group-hover:translate-x-2">
                          {item.title}
                        </h3>
                      </div>
                      <span className="shrink-0 text-secondary transition-transform duration-300 group-data-[state=open]:rotate-45">
                        <Plus className="size-7 md:size-9" strokeWidth={1.75} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pl-0 md:pl-[5.5rem]">
                    <ol className="space-y-3 mb-8 max-w-2xl">
                      {item.steps.map((step, sIdx) => <li key={sIdx} data-index={sIdx} className="flex gap-4 font-default text-base leading-[1.65] text-foreground/80">
                          <span className="font-default text-sm text-secondary shrink-0 pt-1 min-w-[2rem]">
                            {String(sIdx + 1).padStart(2, "0")}.
                          </span>
                          <span>{step}</span>
                        </li>)}
                    </ol>
                    <Link to="/ContactPage" className="inline-flex items-center gap-2 font-default text-[0.75rem] tracking-[0.18em] uppercase text-foreground hover:text-secondary transition-colors duration-200 underline underline-offset-[6px] decoration-1">
                      <Download className="size-3.5" strokeWidth={1.75} />
                      Download Strategy PDF →
                    </Link>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}